import React, { ReactNode } from "react";

import { Space } from "antd";
import cn from "classnames";

import { ExclamationSVG, TickCircleSVG } from "assets/icons";
import { AnswerOption, SelectionOption } from "components/AnswerOption";
import { InputWrapper, InputWrapperProps } from "components/InputWrapper";
import { SelectionType } from "components/SelectionIndicator";

import styles from "./Question.module.scss";

type Props = {
  options: { option: ReactNode; value: string; leftSide?: ReactNode; rightSide?: ReactNode; disabled?: boolean }[];
  value: string | string[];
  onClick: (value: string) => void;
  answerType?: SelectionType;
  wrapperClassName?: string;
  disabled?: boolean;
  dataKeyPrefix?: string;
  description?: React.ReactNode;
  alertText?: React.ReactNode;
  secondaryAlertText?: React.ReactNode;
} & InputWrapperProps;

export const Question = ({
  options,
  value,
  onClick,
  className,
  wrapperClassName,
  answerType,
  disabled,
  dataKeyPrefix,
  description,
  alertText,
  secondaryAlertText,
  ...rest
}: Props) => {
  const renderAnswer = (answer: Props["options"][number], index: number) => {
    const selected = value.includes(answer.value);

    const isDisabled = Boolean(disabled || answer.disabled);

    const onAnswerClick = () => {
      if (isDisabled) return;
      onClick(answer.value);
    };

    if (answerType) {
      return (
        <SelectionOption
          dataKey={dataKeyPrefix ? `${dataKeyPrefix}__${answer.value}` : answer.value}
          key={index}
          type={answerType}
          checked={selected}
          option={answer.option}
          onClick={onAnswerClick}
          disabled={isDisabled}
          rightSide={answer.rightSide}
        />
      );
    }

    return (
      <AnswerOption
        dataKey={dataKeyPrefix ? `${dataKeyPrefix}__${answer.value}` : answer.value}
        key={index}
        option={answer.option}
        onClick={onAnswerClick}
        disabled={isDisabled}
        rightSide={answer.rightSide}
      />
    );
  };

  return (
    <InputWrapper className={cn(styles.inputWrapper, wrapperClassName)} {...rest}>
      {!!description && <div className={styles.description}>{description}</div>}
      <Space direction='vertical' size={6} className={styles.answersSpace}>
        {options?.map((props, idx) => renderAnswer(props, idx))}
      </Space>

      {(!!alertText || !!secondaryAlertText) && (
        <div className={styles.noticePanel}>
          {!!alertText && (
            <div className={styles.noticeRestrict}>
              <ExclamationSVG className={styles.noticeIconRestrict} />
              <div>{alertText}</div>
            </div>
          )}

          {!!secondaryAlertText && (
            <div className={styles.noticeAllow}>
              <TickCircleSVG className={styles.noticeIconAllow} />
              <div>{secondaryAlertText}</div>
            </div>
          )}
        </div>
      )}
    </InputWrapper>
  );
};
